import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BlockTextComponent } from '../../common/block-text/block-text.component';

/**
 * Generic mockup-styled field wrapper for any text field edited via the real
 * app-block-text (Subject, Preheader, Main Banner's link/alt, and future
 * blocks). Per the mockup: the inline field stays a clean plain-text box (no
 * WYSIWYG toolbar); a popout button next to it opens a modal with the full
 * rich-text editor (bold/italic/underline/strike/list) for fields that need
 * formatting. Only one app-block-text is ever mounted at a time (inline OR
 * in the modal), both bound to the same [blockData]/(handleOnChange), so
 * there's no dual-instance sync issue - switching modes just re-mounts.
 *
 * Inputs/outputs mirror app-block-text's own API 1:1 (pure pass-through) so
 * this can drop in wherever app-block-text is used today.
 */
@Component({
  selector: 'app-field-editor-v2',
  imports: [CommonModule, BlockTextComponent],
  templateUrl: './field-editor-v2.component.html',
  styleUrl: './field-editor-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldEditorV2Component {
  @Input() blockData = '';
  @Input() placeholder = '';
  @Input() maxLength = 0;
  @Input() type = 'content';
  @Input() variable!: string;
  @Input() blockId: string | undefined;
  @Input() isImage = false;
  @Input() selectedSegment = '';
  @Input() canBeEmpty = false;
  /** Disables the popout (expanded WYSIWYG editor) button - e.g. while the
   *  template isn't locked for editing. Doesn't touch the inline field itself
   *  (that's still gated the normal way, via the shared .disable-editor class). */
  @Input() disabled = false;
  /** Taller inline box (e.g. Intro + CTA's intro text) instead of the default
   *  single-line look used by Subject/Preheader/Main Banner's fields. */
  @Input() multiline = false;
  /** Reveals app-block-text's own (otherwise hidden inline) character counter
   *  below the field - opt-in per field instead of the default single-line
   *  fields, which hide it to match the mockup's compact boxes. */
  @Input() showCounter = false;

  @Output() handleOnChange = new EventEmitter<string>();
  @Output() handleOnSegmentChange = new EventEmitter<string>();

  protected expanded = false;

  protected openExpanded(): void {
    if (this.disabled) {
      return;
    }
    this.expanded = true;
  }
}
